import { NextResponse, type NextRequest } from 'next/server';

import type { MiddlewareParams } from '@/types/middlewares';
import type { AppConfig } from '@/types/config';
import { COOKIES } from '@/utils/cookies';
import { TIMERS_IN_MS } from '@/utils/constants';

export const DEV_ACCESS_TOKEN = 'poker-dev-token';
// Forwarded by the middleware to the server runtime so layouts / server components
// can render poker-desktop-client-aware UI without re-parsing the URL.
export const POKER_DESKTOP_CLIENT_HEADER = 'x-poker-desktop-client';
const DEV_ACCESS_PATH = '/set-dev-access';
const SKIPPED_PATH_PREFIXES = [
    '/account',
    '/rewards',
    '/api/',
    '/transaction-history',
    '/apple-app-site-association',
    '/.well-known/apple-app-site-association',
    '/.well-known/assetlinks.json'
];
const CERT_OR_QA_ENVS = new Set(['cert', 'qa']);

const getDomain = (req: NextRequest): string => {
    return req.headers.get('x-host') ?? req.headers.get('host') ?? '';
};

const isLauncherSubdomain = (req: NextRequest, configDomain: string): boolean => {
    if (!configDomain) {
        return false;
    }

    const expected = `launcher.${configDomain}`.toLowerCase();
    return getDomain(req).toLowerCase() === expected;
};

const isCertificationOrStagingEnvironment = (envType?: string): boolean => {
    if (!envType) {
        return false;
    }

    return CERT_OR_QA_ENVS.has(envType.toLowerCase());
};

const isAllowedEnvironment = (req: NextRequest): boolean => {
    const domain = getDomain(req).toLowerCase();
    if (!domain) {
        return false;
    }

    if (domain.includes('local.') || domain === 'localhost') {
        return true;
    }

    if (domain.includes('devstack-')) {
        return true;
    }

    if (domain.includes('.fndl.dev')) {
        return domain.includes('integration.') || domain.includes('int.');
    }

    return false;
};

const isDeveloperBypass = (req: NextRequest, envType?: string): boolean => {
    if (!isCertificationOrStagingEnvironment(envType)) {
        return false;
    }

    const devCookie = req.cookies.get(COOKIES.POKER_DEV_ACCESS)?.value;
    return devCookie === DEV_ACCESS_TOKEN;
};

const isNativeApp = (req: NextRequest): boolean => {
    const wrapper = req.cookies.get(COOKIES.WRAPPER)?.value?.toLowerCase();
    return wrapper === 'ios' || wrapper === 'android';
};

const isPokerDesktopClientQueryParam = (req: NextRequest): boolean => {
    if (req.nextUrl.searchParams.get('desktopClient') !== 'true') {
        return false;
    }

    return !req.cookies.get(COOKIES.WRAPPER)?.value;
};

const isGameLaunchPopup = (req: NextRequest): boolean => {
    return req.nextUrl.searchParams.get('switchedToPopup') === 'true';
};

const isPokerUserAgent = (req: NextRequest): boolean => {
    return req.headers.get('user-agent')?.includes('FDPoker') ?? false;
};

const isSkippedPath = (path: string): boolean => {
    return SKIPPED_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
};

const handleSetDevAccess = (req: NextRequest, envType?: string): NextResponse | undefined => {
    if (req.nextUrl.searchParams.get('token') !== DEV_ACCESS_TOKEN) {
        return undefined;
    }

    if (!isCertificationOrStagingEnvironment(envType)) {
        return new NextResponse('Dev access only available for certification and staging environments', {
            status: 200
        });
    }

    const response = new NextResponse('Dev access enabled for 24 hours', { status: 200 });
    response.cookies.set(COOKIES.POKER_DEV_ACCESS, DEV_ACCESS_TOKEN, {
        maxAge: TIMERS_IN_MS.ONE_DAY / 1000,
        path: '/',
        httpOnly: false
    });

    return response;
};

export function pokerAccessMiddleware({ req, res, config }: MiddlewareParams): NextResponse | undefined {
    const { landingPageUrl, domain, unifiedModules } = config as AppConfig;

    if (isPokerDesktopClientQueryParam(req)) {
        res.headers.set(POKER_DESKTOP_CLIENT_HEADER, 'true');
    }

    if (!landingPageUrl) {
        return undefined;
    }

    const path = req.nextUrl.pathname;

    if (isSkippedPath(path)) {
        return undefined;
    }

    if (isLauncherSubdomain(req, domain)) {
        return undefined;
    }

    if (isNativeApp(req) || isPokerUserAgent(req) || isPokerDesktopClientQueryParam(req) || isGameLaunchPopup(req)) {
        return undefined;
    }

    const envType = unifiedModules?.env;

    if (path === DEV_ACCESS_PATH) {
        const devAccessResponse = handleSetDevAccess(req, envType);
        if (devAccessResponse) {
            return devAccessResponse;
        }
        return undefined;
    }

    if (isAllowedEnvironment(req) || isDeveloperBypass(req, envType)) {
        return undefined;
    }

    return NextResponse.redirect(new URL(landingPageUrl), 307);
}
